import { useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import {
  EXCEPTIONS_TAB_SLUG,
  INSPECTIONS_MENU_ITEM,
  INSPECTIONS_SLUG,
  REVIEW_MENU,
  VISION_REVIEW_SLUGS,
} from 'components/exceptions/constants'
import { canViewInspections } from 'data/permissions/permissionsSelectors'
import { selectIsVisionReviewEnabled } from 'data/tenant/tenantSettingsSelectors'

import type { SideNavMenuItem } from 'components/common/side-nav/MenuGroup'
import type { ExceptionsTabSlug } from 'components/exceptions/constants'

interface ReviewAccess {
  isReviewEnabled: boolean
  menuItems: SideNavMenuItem[]
  allowedSlugs: ExceptionsTabSlug[]
  defaultSlug: ExceptionsTabSlug
}

/**
 * Which Review sections this user can open: the Vision pages follow the visionReview tenant flag, Inspections the
 * Inspections permission. The router, the side nav and the slug guard all read this, so they cannot drift apart.
 */
export const useReviewAccess = (): ReviewAccess => {
  const isVisionReviewEnabled = useSelector(selectIsVisionReviewEnabled, shallowEqual)
  const canSeeInspections = useSelector(canViewInspections, shallowEqual)

  return useMemo(() => {
    const allowedSlugs = [...(isVisionReviewEnabled ? VISION_REVIEW_SLUGS : []), ...(canSeeInspections ? [INSPECTIONS_SLUG] : [])]
    return {
      isReviewEnabled: allowedSlugs.length > 0,
      menuItems: [...(isVisionReviewEnabled ? REVIEW_MENU : []), ...(canSeeInspections ? [INSPECTIONS_MENU_ITEM] : [])],
      allowedSlugs,
      defaultSlug: allowedSlugs[0] ?? EXCEPTIONS_TAB_SLUG,
    }
  }, [isVisionReviewEnabled, canSeeInspections])
}
